import React from "react";
import { Alert, Button, Card, CardBody, Container } from "reactstrap";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import BreadCrumb from "Components/Common/BreadCrumb";
import { APP_NAME_SHORT } from "common/constants";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import { useProfile } from "Components/Hooks/UserHooks";
import { isAllowed } from "common/lib/permissionUtils";
import { normalize } from "common/lib/normalize";
import {
  getContractor,
  getContractors,
  getContractorSummary,
  mergeContractors,
} from "common/services/contractors.service";
import { getLTAs } from "common/services/ltas.service";
import { usePseaReader } from "pages/Assessments/PSEAAssessments/usePseaReader";
import CompareHeader from "./CompareHeader";
import { CompareSection } from "./CompareLayout";
import ConfirmMergeModal from "./ConfirmMergeModal";
import {
  buildSections,
  countMoving,
  describeTransfers,
} from "./buildSections";
import { pseaDecidedByDate, pseaKeptSide } from "./mergeRules";

const LTA_VIEW_CHECKS = [{ resource: "lta", action: "find" }];

// How many LTAs each side lists before collapsing the rest into a count.
const LTA_PREVIEW_LIMIT = 5;

const CONTRACTOR_QUERY = {
  fields: [
    "contractorName",
    "acronym",
    "createdAt",
    "updatedAt",
    "website",
    "rmuFlag",
    "flagDate",
    "flaggingJustification",
  ],
  populate: {
    organizationType: { fields: ["name"] },
    previousOrAliasName: true,
    contracts: { count: true },
    performanceRatings: {
      populate: { performanceRatingType: true, ratedBy: true },
    },
    semgMentions: true,
    boardOfDirectors: { populate: { person: true } },
    contactPersons: true,
    contractorRegistration: true,
    phoneNumber: true,
    emailAddress: true,
    physicalAddress: true,
    postalAddress: true,
  },
};

interface SideState {
  record: any;
  summary: any;
  ltas: any;
  psea: any;
  loading: boolean;
  error: boolean;
}

const EMPTY_SIDE: SideState = {
  record: null,
  summary: null,
  ltas: null,
  psea: null,
  loading: false,
  error: false,
};

/**
 * Loads everything the page shows for one contractor. Responses for a
 * contractor that is no longer selected are dropped, so switching the
 * contractor to merge quickly cannot leave the page showing a stale record.
 */
const useContractorSide = (
  id: string | null,
  loadLtas: (id: any) => Promise<any>,
  loadPsea: (id: any) => Promise<any>
) => {
  const [state, setState] = React.useState<SideState>(EMPTY_SIDE);

  React.useEffect(() => {
    if (!id) {
      setState(EMPTY_SIDE);
      return;
    }
    let cancelled = false;
    setState({ ...EMPTY_SIDE, loading: true });

    (async () => {
      try {
        const response: any = await getContractor(id, CONTRACTOR_QUERY);
        // The summary and previews are supplementary: a failure there leaves
        // those figures empty rather than blocking the merge.
        const [summary, ltas, psea] = await Promise.all([
          getContractorSummary({ id }).catch(() => null),
          loadLtas(id),
          loadPsea(id),
        ]);
        if (cancelled) return;
        setState({
          record: normalize(response.data),
          summary,
          ltas,
          psea,
          loading: false,
          error: false,
        });
      } catch (e) {
        if (cancelled) return;
        setState({ ...EMPTY_SIDE, error: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, loadLtas, loadPsea]);

  return state;
};

const MergeContractor: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: mainId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  document.title = `Merge Contractors | ${APP_NAME_SHORT}`;
  usePageActivity(location.pathname, "Merge Contractor", mainId || null);

  // The contractor to merge lives in the URL, so a refresh or a link from the
  // duplicate finder lands on the same comparison.
  const requestedMerge = searchParams.get("target");
  const mergeId =
    requestedMerge && requestedMerge !== String(mainId) ? requestedMerge : null;

  const { userProfile } = useProfile();
  const canViewLtas = isAllowed(userProfile?.permissions, LTA_VIEW_CHECKS);
  const { canRead: canViewPsea, fetchByContractor: fetchPsea } =
    usePseaReader();

  const loadLtas = React.useCallback(
    async (contractorId: any) => {
      if (!canViewLtas) return null;
      try {
        const response: any = await getLTAs({
          filters: { contractor: { id: { $eq: contractorId } } },
          pagination: { page: 1, pageSize: LTA_PREVIEW_LIMIT },
          sort: ["endDate:desc"],
          populate: ["leadAgency"],
        });
        return {
          items: normalize(response.data) || [],
          total: response?.meta?.pagination?.total || 0,
        };
      } catch (e) {
        return null;
      }
    },
    [canViewLtas]
  );

  const loadPsea = React.useCallback(
    async (contractorId: any) => {
      if (!fetchPsea) return null;
      try {
        const response: any = await fetchPsea(contractorId, {
          populate: ["agency"],
        });
        return response?.data ? normalize(response.data) : null;
      } catch (e) {
        return null;
      }
    },
    [fetchPsea]
  );

  const main = useContractorSide(mainId || null, loadLtas, loadPsea);
  const merge = useContractorSide(mergeId, loadLtas, loadPsea);

  const [onlyDifferences, setOnlyDifferences] = React.useState(false);

  // The floating feedback button sits where the sticky merge bar's buttons
  // land; lift it clear of the bar while this page is open.
  React.useEffect(() => {
    document.body.classList.add("merge-bar-active");
    return () => document.body.classList.remove("merge-bar-active");
  }, []);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [merging, setMerging] = React.useState(false);

  const loadOptions = React.useCallback(
    async (inputValue: string) => {
      try {
        const response: any = await getContractors({
          filters: {
            $and: [
              { contractorName: { $containsi: inputValue || "" } },
              mainId ? { id: { $ne: mainId } } : {},
            ],
          },
          pagination: { pageSize: 20, page: 1 },
          sort: ["contractorName:asc"],
          fields: ["contractorName"],
        });
        const list = normalize(response.data) || [];
        return list.map((c: any) => ({ value: c.id, label: c.contractorName }));
      } catch (e) {
        return [];
      }
    },
    [mainId]
  );

  const selectMerge = (option: any) => {
    const next = new URLSearchParams(searchParams);
    if (option?.value) next.set("target", String(option.value));
    else next.delete("target");
    setSearchParams(next, { replace: true });
  };

  const ready = !!main.record && !!merge.record;

  const bothHavePsea = !!main.psea && !!merge.psea;
  const keptSide = bothHavePsea ? pseaKeptSide(main.psea, merge.psea) : null;
  const decidedByDate =
    bothHavePsea && pseaDecidedByDate(main.psea, merge.psea);

  const sections = React.useMemo(
    () =>
      ready
        ? buildSections({
            main,
            merge,
            canViewLtas,
            canViewPsea,
            pseaKeptSide: keptSide,
          })
        : [],
    [ready, main, merge, canViewLtas, canViewPsea, keptSide]
  );

  const movingItems = React.useMemo(
    () => countMoving(merge, canViewLtas),
    [merge, canViewLtas]
  );
  const movingTotal = movingItems.reduce((sum, item) => sum + item.count, 0);
  const transfers = describeTransfers(main, merge);

  // The one-line outcome in the action bar. A flag or alias transfer counts as
  // something moving, so a merge that only carries those never reads as empty.
  const outcomeParts = [
    movingTotal > 0
      ? `${movingTotal.toLocaleString()} ${
          movingTotal === 1 ? "record moves" : "records move"
        }`
      : null,
    transfers.flag === "moves"
      ? "RMU flag moves"
      : transfers.flag === "reasons"
      ? "flag reasons added"
      : null,
    transfers.aliases > 0
      ? `${transfers.aliases} ${
          transfers.aliases === 1 ? "name kept" : "names kept"
        } as ${transfers.aliases === 1 ? "an alias" : "aliases"}`
      : null,
  ].filter(Boolean);
  const outcomeSummary =
    outcomeParts.length > 0
      ? outcomeParts.join(" · ")
      : "only profile details are combined";
  const aliasNote =
    transfers.aliases > 0
      ? `${transfers.aliases} ${
          transfers.aliases === 1
            ? "name from the merged contractor is"
            : "names from the merged contractor are"
        } kept as ${
          transfers.aliases === 1 ? "an alias" : "aliases"
        }, so searches and screening still find it.`
      : null;

  const comparableRows = sections.flatMap((section) =>
    section.rows.filter((row) => !row.empty)
  );
  const differences = ready
    ? {
        differing: comparableRows.filter((row) => !row.same).length,
        total: comparableRows.length,
      }
    : null;

  // Swapping keeps the same pair but changes which record survives.
  const onSwap =
    ready && mainId && mergeId
      ? () => {
          setOnlyDifferences(false);
          navigate(`/contractors/merge/${mergeId}?target=${mainId}`, {
            replace: true,
          });
        }
      : null;

  const visibleSections = sections
    .map((section) => ({
      section,
      rows: section.rows.filter(
        (row) => !row.empty && !(onlyDifferences && row.same)
      ),
    }))
    .filter(({ rows }) => rows.length > 0);

  // Say what is not shown, so a missing section never reads as an oversight.
  const unrecordedSections = sections
    .filter((section) => section.rows.every((row) => row.empty))
    .map((section) => section.title);
  const matchingRowsHidden = onlyDifferences
    ? sections.reduce(
        (sum, section) =>
          sum + section.rows.filter((row) => !row.empty && row.same).length,
        0
      )
    : 0;

  const flagNote =
    merge.record?.rmuFlag && !main.record?.rmuFlag
      ? "The RMU flag on the merged contractor moves to the main contractor, with its justification and documents."
      : merge.record?.rmuFlag && main.record?.rmuFlag
      ? "Both contractors are flagged. The merged contractor's flag reasons and documents are added to the main contractor's flag."
      : null;

  const pseaNote = !canViewPsea
    ? null
    : bothHavePsea
    ? `Both have a PSEA assessment. The ${
        keptSide === "duplicate" ? "merged contractor's" : "main contractor's"
      } is kept${
        decidedByDate ? " as the more recent one" : ""
      }; the other's history is added to it.`
    : merge.psea
    ? "The PSEA assessment moves to the main contractor."
    : null;

  const onConfirmMerge = async () => {
    if (!mainId || !mergeId) return;
    try {
      setMerging(true);
      await mergeContractors({
        id: String(mainId),
        contractorId: String(mergeId),
      });
      toast.success(
        `${merge.record?.contractorName || "Contractor"} was merged into ${
          main.record?.contractorName || "the main contractor"
        }`
      );
      setConfirmOpen(false);
      navigate(`/contractors/${mainId}`);
    } catch (e: any) {
      // The merge runs as a series of updates, not one transaction, so a
      // failure part-way can leave some records already moved.
      toast.error(
        "The merge did not finish. Some records may already have moved to the main contractor — reload both profiles and check before trying again.",
        { autoClose: false }
      );
    } finally {
      setMerging(false);
    }
  };

  const picker = (
    <AsyncSelect
      cacheOptions
      defaultOptions
      isClearable
      loadOptions={loadOptions}
      onChange={selectMerge}
      value={
        mergeId
          ? {
              value: mergeId,
              label: merge.record?.contractorName || "Loading…",
            }
          : null
      }
      placeholder="Search for the contractor to merge…"
      aria-label="Contractor to merge"
      menuPortalTarget={document.body}
      styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 1060 }) }}
    />
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Merge Contractors"
            pageTitles={[
              { title: "Dashboard", link: "/" },
              { title: "Contractors", link: "/contractors" },
            ]}
            back
          />

          {main.error ? (
            <Alert color="danger">
              The main contractor could not be loaded. It may have been deleted
              or merged already.
            </Alert>
          ) : null}

          <CompareHeader
            main={main}
            merge={merge}
            picker={picker}
            onlyDifferences={onlyDifferences}
            onToggleDifferences={setOnlyDifferences}
            differences={differences}
            onSwap={onSwap}
          />

          {merge.error ? (
            <Alert color="danger">
              The selected contractor could not be loaded. It may have been
              deleted or merged already — choose another.
            </Alert>
          ) : null}

          {ready ? (
            visibleSections.length > 0 ? (
              <>
                {visibleSections.map(({ section, rows }) => (
                  <CompareSection
                    key={section.key}
                    section={section}
                    rows={rows}
                  />
                ))}
                {unrecordedSections.length > 0 || matchingRowsHidden > 0 ? (
                  <div className="text-ink-muted fs-12 mb-3 px-1 vstack gap-1">
                    {unrecordedSections.length > 0 ? (
                      <span>
                        <i className="ri-eye-off-line align-bottom me-1"></i>
                        Not recorded on either contractor:{" "}
                        {unrecordedSections.join(", ")}.
                      </span>
                    ) : null}
                    {matchingRowsHidden > 0 ? (
                      <span>
                        <i className="ri-filter-3-line align-bottom me-1"></i>
                        {matchingRowsHidden} matching{" "}
                        {matchingRowsHidden === 1 ? "row" : "rows"} hidden.
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : (
              <Card>
                <CardBody className="text-center text-ink-muted py-5">
                  <i className="ri-equalizer-line fs-24 d-block mb-2"></i>
                  No differences between these two contractors.
                </CardBody>
              </Card>
            )
          ) : merge.loading || (main.loading && mergeId) ? (
            <div aria-busy="true" aria-label="Loading comparison">
              {[0, 1].map((i) => (
                <Card className="compare-section" key={i}>
                  <div className="compare-section__header placeholder-glow">
                    <span className="placeholder col-2"></span>
                  </div>
                  {[0, 1, 2].map((j) => (
                    <div className="compare-grid compare-row placeholder-glow" key={j}>
                      <div className="compare-row__label">
                        <span className="placeholder col-8"></span>
                      </div>
                      <div className="compare-row__cell">
                        <span className="placeholder col-6"></span>
                      </div>
                      <div className="compare-row__cell">
                        <span className="placeholder col-5"></span>
                      </div>
                    </div>
                  ))}
                </Card>
              ))}
            </div>
          ) : !merge.error && !main.loading ? (
            <Card>
              <CardBody className="compare-empty">
                <div className="avatar-md mx-auto mb-3">
                  <div className="avatar-title rounded-circle bg-primary-subtle text-primary fs-24">
                    <i className="ri-git-merge-line"></i>
                  </div>
                </div>
                <h5 className="fs-16 mb-2">Choose a contractor to compare</h5>
                <p className="text-ink-muted mb-0 mx-auto" style={{ maxWidth: 460 }}>
                  Use the search box in the right-hand column to pick the
                  duplicate of {main.record?.contractorName || "this contractor"}.
                  The two records are shown side by side, and nothing changes
                  until you confirm the merge.
                </p>
              </CardBody>
            </Card>
          ) : null}

          <Card className="merge-action-bar mb-0">
            <CardBody className="merge-action-bar__body">
              <div className="merge-action-bar__summary">
                {ready ? (
                  <>
                    <span className="text-ink-muted">Merge </span>
                    <strong>{merge.record?.contractorName}</strong>
                    <span className="text-ink-muted"> into </span>
                    <strong>{main.record?.contractorName}</strong>
                    <span className="text-ink-muted"> · {outcomeSummary}</span>
                  </>
                ) : (
                  <span className="text-ink-muted">
                    <i className="ri-information-line align-bottom me-1"></i>
                    Choose a contractor to merge to review the outcome.
                  </span>
                )}
              </div>
              <div className="merge-action-bar__actions">
                <Button
                  color="light"
                  className="material-shadow-none"
                  onClick={() => navigate(`/contractors/${mainId}`)}
                  disabled={merging}
                >
                  Cancel
                </Button>
                <Button
                  color="danger"
                  className="material-shadow-none"
                  disabled={!ready || merging}
                  onClick={() => setConfirmOpen(true)}
                >
                  <i className="ri-git-merge-line align-bottom me-1"></i>
                  Merge contractors
                </Button>
              </div>
            </CardBody>
          </Card>

          <ConfirmMergeModal
            isOpen={confirmOpen}
            merging={merging}
            mainName={main.record?.contractorName}
            mergeName={merge.record?.contractorName}
            items={movingItems}
            pseaNote={pseaNote}
            flagNote={flagNote}
            aliasNote={aliasNote}
            onCancel={() => setConfirmOpen(false)}
            onConfirm={onConfirmMerge}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MergeContractor;
